import { forwardRef, useEffect, useRef } from "react";
import i18nPackage from "../../i18n/i18n";
import { BasicStep } from "../components/basic-step";
import {
	BaseSubstepProps,
	BasicSubstep,
	BasicSubstepDescription,
	BasicSubstepTitle,
	getBasicSubstepIcon,
} from "../components/basic-substep";
import Guide from "../components/guide";
import styles from "./index.module.css";
import {
	BeforeQuizProgressBar,
	CameraPreview,
	CameraPreviewProps,
	DeviceDropdown,
	DeviceDropdownProps,
	getCameraDescriptionError,
	getCameraTitleError,
} from "../components/media-components";
import { CoreButton } from "../../core";
import StartRoomScan from "../../images/room-scan.svg?react";
import RetakeIcon from "../../images/retake.svg?react";
import { Button, Divider, SvgIcon, Typography } from "@mui/material";
import RecordingActiveIcon from "../../images/recording-active.svg?react";

function RoomScanGuide() {
	return (
		<Guide
			description={i18nPackage.t("beforeQuiz.roomScan.scanRoom.guide")}
			iframeTitle={i18nPackage.t("beforeQuiz.roomScan.scanRoom.videoTitle")}
			src="https://demo.video.yuja.com/V/Video?v=14648629&node=63113967&a=19001577"
			title={i18nPackage.t("beforeQuiz.roomScan.scanRoom.guideTitle")}
			className={styles.roomScanGuide}
		/>
	);
}

type RoomScanSubstepProps = BaseSubstepProps & {
	onStartRoomScan: () => void;
	elapsedTimeSec?: number;
	videoUrl?: string;
	uploading: boolean;
	slotProps: {
		cameraPreview: CameraPreviewProps;
		cameraDropdown: Omit<DeviceDropdownProps, "type">;
	};
};

export function RoomScanSubstep({
	status,
	onStartRoomScan,
	elapsedTimeSec,
	videoUrl,
	uploading,
	slotProps,
	rootRef,
}: RoomScanSubstepProps) {
	const { devices, onChange, selectedDeviceId, container: dropdownContainer } = slotProps.cameraDropdown;
	const { cameraError, isVideoLoading } = slotProps.cameraPreview;
	const buttonText =
		status === "completed"
			? i18nPackage.t("beforeQuiz.roomScan.scanRoom.retakeRoomScan")
			: i18nPackage.t("beforeQuiz.roomScan.scanRoom.startRoomScan");

	const title = cameraError
		? getCameraTitleError(cameraError)
		: i18nPackage.t(`beforeQuiz.roomScan.scanRoom.title.${status}`);

	const description = cameraError
		? getCameraDescriptionError(cameraError)
		: i18nPackage.t(`beforeQuiz.roomScan.scanRoom.description.${status}`);

	// This button replaces "Finish" on completion, and goes disabled then re-enabled on Retake (camera
	// reloading) — both times React/the browser drop focus. Track "showing AND enabled" so either
	// transition re-focuses the button once it's actually usable.
	const showsStartOrRetakeButton = !uploading && !(status === "started" && !isVideoLoading);
	const isStartOrRetakeButtonDisabled = status === "completed" ? false : !!cameraError || isVideoLoading;
	const canFocusStartOrRetakeButton = showsStartOrRetakeButton && !isStartOrRetakeButtonDisabled;
	const startOrRetakeButtonRef = useRef<HTMLButtonElement>(null);
	const couldFocusStartOrRetakeButton = useRef(canFocusStartOrRetakeButton);

	useEffect(() => {
		if (!couldFocusStartOrRetakeButton.current && canFocusStartOrRetakeButton) {
			startOrRetakeButtonRef.current?.focus();
		}
		couldFocusStartOrRetakeButton.current = canFocusStartOrRetakeButton;
	}, [canFocusStartOrRetakeButton]);

	return (
		<BasicSubstep ref={rootRef}>
			<div className={styles.roomScanWrapper}>
				<div className={styles.roomScanTopRow}>
					{getBasicSubstepIcon(status)}
					<div className={styles.roomScanLeftRegion}>
						<BasicSubstepTitle title={title} />
						<BasicSubstepDescription description={description} className={styles.roomScanDescription} />
						{(status === "failed" || status === "unstarted") && (
							<DeviceDropdown
								devices={devices}
								onChange={onChange}
								selectedDeviceId={selectedDeviceId}
								container={dropdownContainer}
								type="camera"
								disabled={isVideoLoading && !cameraError}
							/>
						)}
					</div>
				</div>
				<div className={styles.roomScanRightRegion}>
					{status === "completed" && videoUrl ? (
						<RoomScanPreview videoUrl={videoUrl} />
					) : (
						<CameraPreview {...slotProps.cameraPreview} />
					)}
					{!uploading &&
						(status === "started" && !isVideoLoading ? (
							<div className={styles.roomScanControlsContainer}>
								<div className={styles.stopRecordContainer}>
									<Button
										onClick={onStartRoomScan}
										className={styles.stopRecording}
										startIcon={<RecordingActiveIcon />}
										// Room scan video must be 1 sec long
										disabled={!elapsedTimeSec || elapsedTimeSec < 1}
									>
										<Typography>{i18nPackage.t("beforeQuiz.roomScan.scanRoom.finish")}</Typography>
									</Button>
								</div>
								<Divider orientation="vertical" color="#737373" />
								<Typography className={styles.timerText}>
									{elapsedTimeSec ? formatDurationFromSeconds(elapsedTimeSec) : "00:00:00"}
								</Typography>
							</div>
						) : (
							<CoreButton
								ref={startOrRetakeButtonRef}
								onClick={onStartRoomScan}
								className={styles.roomScanButton}
								startIcon={
									<SvgIcon fill="#42296E" width={24} height={24} aria-hidden="true">
										{status === "completed" ? <RetakeIcon /> : <StartRoomScan />}
									</SvgIcon>
								}
								// Button is disabled if there is a camera error, or the camera preview is loading
								// Will not be disabled if status is complete
								disabled={status === "completed" ? false : !!cameraError || isVideoLoading}
							>
								{buttonText}
							</CoreButton>
						))}
					{uploading && <BeforeQuizProgressBar error={status === "failed"} className={styles.roomScanProgressBar} />}
				</div>
			</div>
			{!uploading && <RoomScanGuide />}
		</BasicSubstep>
	);
}

type RoomScanPreviewProps = {
	videoUrl: string;
};

function RoomScanPreview({ videoUrl }: RoomScanPreviewProps) {
	return <video className={styles.roomScanVideo} src={videoUrl} controls controlsList="nodownload" />;
}

function formatDurationFromSeconds(seconds: number): string {
	const totalSeconds = Math.floor(seconds);
	const minutes = Math.floor(totalSeconds / 60);
	const remaining = totalSeconds % 60;
	return `${String(minutes).padStart(2, "0")}:${String(remaining).padStart(2, "0")}`;
}

export const RoomScanStep = forwardRef<HTMLDivElement, { children: React.ReactNode }>(function RoomScanStep(
	{ children },
	ref,
) {
	return (
		<BasicStep description={i18nPackage.t("beforeQuiz.roomScan.description")} ref={ref}>
			{children}
		</BasicStep>
	);
});

export default RoomScanStep;
